// Settings data model + vocabularies — host-owned mock contract (ADR-0014).
//
// Ported verbatim from the pbdesign wireframe `data/settings.ts` (the behaviour
// oracle). Everything here is mock-only: the host `settingsApi` mock seeds from
// these shapes and the pages read/write through TanStack Query. The live swap to
// `/v1/settings/*` replaces the mock client, not these types.

import { SETTINGS_MODULES } from "./roster.js";

// ── Durable platform presentation preferences ───────────────────────────────

import type { PlatformTheme } from "@playbookai/types";

/** Theme vocabulary is shared with Playbook BE through @playbookai/types (PB-5134). */
export type PresentationTheme = PlatformTheme;

/** Playbook BE-owned personal presentation state. Version 0 is the server's
 * unpersisted default; the first successful replacement creates version 1. */
export interface PresentationPrefs {
  theme: PresentationTheme;
  locale: string;
  timeZone: string;
  version: number;
  updatedAt: string | null;
}

export interface ReplacePresentationPrefs {
  theme: PresentationTheme;
  locale: string;
  timeZone: string;
  expectedVersion: number;
}

// ── PlayerAI preferences ──────────────────────────────────────────────────────

export const PLAYERAI_MODES = ["ask-first", "assist", "act"] as const;
export type PlayerAiModePref = (typeof PLAYERAI_MODES)[number];

export const PLAYERAI_MODE_LABELS: Record<PlayerAiModePref, string> = {
  "ask-first": "Ask first",
  assist: "Assist",
  act: "Act",
};

export const PLAYERAI_MODE_HINTS: Record<PlayerAiModePref, string> = {
  "ask-first": "Suggests actions and waits for your approval.",
  assist: "Answers and prepares changes without applying them.",
  act: "Completes allowed actions, asking for approval when required.",
};

export const PLAYERAI_TONES = [
  "Professional",
  "Coaching",
  "Direct",
  "Encouraging",
  "Formal",
] as const;
export type PlayerAiTone = (typeof PLAYERAI_TONES)[number];

export const PLAYERAI_VERBOSITY = ["Concise", "Balanced", "Detailed"] as const;
export type PlayerAiVerbosity = (typeof PLAYERAI_VERBOSITY)[number];

export interface PlayerAiPrefs {
  defaultMode: PlayerAiModePref;
  tone: PlayerAiTone;
  verbosity: PlayerAiVerbosity;
  /** Per-module PlayerAI enablement, keyed by module id. */
  enabledByModule: Record<string, boolean>;
}

// ── Launcher preferences ──────────────────────────────────────────────────────

export interface LandingOption {
  value: string;
  label: string;
}

export const LANDING_OPTIONS: LandingOption[] = [
  { value: "home", label: "Home dashboard" },
  { value: "launcher", label: "App launcher" },
  ...SETTINGS_MODULES.map((m) => ({ value: m.id, label: m.name })),
];

/**
 * Live Playbook BE launcher preference (PB-5134, d22): a sibling of the
 * presentation preference with its own compare-and-swap version. `favourites`
 * are module ids the shell filters against its own roster; ids it does not
 * know are ignored, never rejected.
 */
export interface LauncherPrefs {
  favourites: readonly string[];
  /** True after the user explicitly adds, removes, reorders, or restores modules. */
  moduleRosterConfigured: boolean;
  showMoreApps: boolean;
  /** `home`, `launcher`, or a module id (a `LandingOption` value). */
  defaultLanding: string;
  /** 0 until a row exists; the compare-and-swap precondition afterwards. */
  version: number;
  updatedAt: string | null;
}

/** The server's own defaults for an unpersisted launcher row (version 0):
 * what "Reset to defaults" restores (PB-5134, t11). */
export const LAUNCHER_DEFAULTS: Omit<LauncherPrefs, "version" | "updatedAt"> = {
  favourites: [],
  moduleRosterConfigured: false,
  showMoreApps: false,
  defaultLanding: "home",
};

export interface ReplaceLauncherPrefs {
  favourites: readonly string[];
  moduleRosterConfigured: boolean;
  showMoreApps: boolean;
  defaultLanding: string;
  expectedVersion: number;
}

// ── Notification preferences ──────────────────────────────────────────────────

export interface NotificationChannels {
  email: boolean;
  inApp: boolean;
}

export interface ModuleAlert {
  id: string;
  label: string;
  description: string;
}

export const MODULE_ALERTS: ModuleAlert[] = [
  {
    id: "dpm-thresholds",
    label: "DPM threshold breaches",
    description:
      "Alert when a daily performance metric crosses its target band.",
  },
  {
    id: "hoshin-reviews",
    label: "Hoshin review cadence",
    description: "Remind me when a Hoshin plan is due for its periodic review.",
  },
  {
    id: "solve-updates",
    label: "SOLVE updates",
    description:
      "Notify on phase changes and comments in problem-solving projects.",
  },
  {
    id: "lama-campaigns",
    label: "LAMA campaign activity",
    description: "Notify when assessment responses or evidence are submitted.",
  },
  {
    id: "kpi-anomalies",
    label: "KPI anomalies",
    description: "Flag unexpected swings in tracked KPI dashboards.",
  },
];

export const DIGEST_FREQUENCIES = [
  "off",
  "daily",
  "weekly",
  "monthly",
] as const;
export type DigestFrequency = (typeof DIGEST_FREQUENCIES)[number];

export const DIGEST_FREQUENCY_LABELS: Record<DigestFrequency, string> = {
  off: "No digest",
  daily: "Daily digest",
  weekly: "Weekly digest",
  monthly: "Monthly digest",
};

export interface NotificationPrefs {
  channels: NotificationChannels;
  /** Per-alert enablement, keyed by `ModuleAlert` id. */
  alertsById: Record<string, boolean>;
  digest: DigestFrequency;
}

// ── Workspace / Org config ────────────────────────────────────────────────────

export const WORKSPACE_ROLES = ["Owner", "Admin", "Member", "Viewer"] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

export interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  role: WorkspaceRole;
  /** Pending invite (not yet accepted). */
  pending?: boolean;
}

export interface WorkspacePrefs {
  members: WorkspaceMember[];
  /** Org-wide module enablement, keyed by module id. */
  moduleEnabled: Record<string, boolean>;
}

// ── Full settings shape ───────────────────────────────────────────────────────

/** User-scoped personal preferences still held by the in-memory mock (any
 * member edits their own). Presentation and launcher preferences are live on
 * Playbook BE and deliberately outside this shape. */
export interface PersonalSettings {
  playerai: PlayerAiPrefs;
  notifications: NotificationPrefs;
}

export interface SettingsData extends PersonalSettings {
  /** Tenant-scoped, admin-only. */
  workspace: WorkspacePrefs;
}

/** Coarse merge patch — any subset of the four top-level sections. */
export type SettingsPatch = Partial<SettingsData>;
